import { useEffect, useState, useCallback } from "react";
import { useCoins } from "../../hooks/useCoins";
import { useBalance } from "../../hooks/useBalance";
import { usePaymentAccount } from "../../hooks/usePaymentAccounts";
import { usePostInswitch } from "../../hooks/useInswitch";
import { useLimits } from "../../hooks/useLimits";
import { usePostWalletWithdraw } from "../../hooks/useWallets";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { numberWithCommas } from "../../utils/functions";
import { FaCirclePlus } from "react-icons/fa6";
import { LuInspect } from "react-icons/lu";
import useModalStore from "../../stores/ModalStore";
import SelectAdvanced from "../../components/SelectAdvanced/SelectAdvanced";
import FeeLimits from "../../components/FeeLimits/FeeLimits";
import InOutHistory from "../../components/InOutHistory/InOutHistory";
import ConfirmationCodeModal from "../../modals/ConfirmationCodeModal/ConfirmationCodeModal";
import PaymentAccountModal from "../../modals/PaymentAccountModal/PaymentAccountModal";
import "./Withdraw.css";
import LoadingModal from "../../modals/LoadingModal/LoadingModal";
import PaymentAccountInfoModal from "../../modals/PaymentAccountInfoModal/PaymentAccountInfoModal";
import Spinner from "../../components/Spinner/Spinner";

// Debounce utility function
const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return function (...args: any[]) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

const Withdraw = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { coin } = useParams<{ coin: string }>();
    const { mutateAsync: withdrawalInswitchRequest } = usePostInswitch();
    const { mutateAsync: withdrawalCryptoRequest } = usePostWalletWithdraw();
    const { data: coinsData } = useCoins();
    const { data: balanceData } = useBalance();
    const { data: paymentAccounts } = usePaymentAccount();
    const { data: limitsData } = useLimits();
    const [displayedError, setDisplayedError] = useState<string | null>(null);


    const BANK_TRANSFER_PAYMENT_METHOD = "BANKTRANSFER";
    const BLOCKCHAIN_PAYMENT_METHOD_MAPPER: { [key: string]: string } = {
        BTC: "BLOCKCHAIN_BTC",
        ETH: "BLOCKCHAIN_ETH",
        TRX: "BLOCKCHAIN_TRX",
    };

    const [_, setBalanceAfterWithdrawal] = useState(0);
    const [withdrawalAmount, setWithdrawalAmount] = useState<number>(0);
    const [inputValue, setInputValue] = useState<string>("");
    const [selectedCoin, setSelectedCoin] = useState(coin || "USDT");
    const [selectedPaymentAccount, setselectedPaymentAccount] = useState<{ [key: string]: any }>();
    const [availableBalance, setAvailableBalance] = useState(0);
    const [validAmount, setValidAmount] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [filteredPaymentAccounts, setFilteredPaymentAccounts] = useState<Array<{ [key: string]: any }>>([]);
    const [inputError, setInputError] = useState<string | null>(null);
    const [showDelayedError, setShowDelayedError] = useState(false);

    const paymentAccountKey =
        selectedPaymentAccount?.type === "fiat" ||
            selectedPaymentAccount?.type === "bank_transfer" ||
            selectedPaymentAccount?.type === "card" 
            ? BANK_TRANSFER_PAYMENT_METHOD
            : selectedPaymentAccount?.blockchain
                ? BLOCKCHAIN_PAYMENT_METHOD_MAPPER[selectedPaymentAccount?.blockchain]
                : 0;

    const { setModalContent, closeModal } = useModalStore((state) => ({
        setModalContent: state.setModalContent,
        closeModal: state.closeModal,
    }));

    useEffect(() => {
        setAvailableBalance(balanceData?.balance[selectedCoin]?.actual || 0);
        setBalanceAfterWithdrawal(balanceData?.balance[selectedCoin]?.actual || 0);
    }, [selectedCoin, balanceData]);

    useEffect(() => {
        setWithdrawalAmount(0);
        setInputValue("");
        setInputError(null);
    }, [selectedCoin]);

    useEffect(() => {
        setBalanceAfterWithdrawal(balanceData?.balance[selectedCoin]?.actual - withdrawalAmount);
    }, [withdrawalAmount]);

    useEffect(() => {
        if (!paymentAccounts) return;
        const filtered = paymentAccounts.filter((paymentAccount: any) => paymentAccount.currency === selectedCoin);
        setFilteredPaymentAccounts(filtered);
    }, [paymentAccounts, selectedCoin]);

    const coinsItems = () => {
        let items: {
            [key: string]: { name: string; price?: number; logo?: string; decimals?: number };
        } = {};
        Object.keys(coinsData ? coinsData.coins : {}).forEach((key) => {
            items[key] = {
                name: coinsData.coins[key].name,
                logo: coinsData.coins[key].logo,
                price: balanceData
                    ? balanceData.balance[key]
                        ? balanceData.balance[key].actual + balanceData.balance[key].orders
                        : "0"
                    : "0",
                decimals: coinsData.coins[key].decimals,
            };
        });
        return items;
    };

    const paymentAccountsItems = paymentAccounts
        ? Object.fromEntries(
            paymentAccounts
                .filter((paymentAccount: { [key: string]: any }) => paymentAccount.currency === selectedCoin)
                .map((paymentAccount: { [key: string]: any }) => [
                    paymentAccount.display_name,
                    {
                        id: paymentAccount.display_name,
                        name: paymentAccount.display_name,
                        description: paymentAccount.blockchain,
                        data: paymentAccount.fields,
                    },
                ])
        )
        : {};

    const handleCoinChange = (value: string) => {
        if (!value) return;
        setSelectedCoin(value);
        setselectedPaymentAccount(undefined);
        setInputValue("");
        setWithdrawalAmount(0);
        navigate(`/withdraw/${value}`);
    };

    const handleSelectedPaymentAccountChange = (value: string) => {
        setselectedPaymentAccount(
            paymentAccounts?.filter(
                (paymentAccount: { [key: string]: any }) =>
                    paymentAccount.display_name === value && paymentAccount.currency === selectedCoin
            )[0]
        );
    };

    const handleLimitCheck = (valid: boolean) => {
        setValidAmount(valid);
    };

    // Debounced amount change handler
    const handleAmountChange = useCallback(debounce((value: string) => {
        if (!value) {
            setWithdrawalAmount(0);
            setInputError(null);
            return;
        }

        // Get coin decimals (default to 8 if undefined)
        const decimals = coinsData?.coins[selectedCoin]?.decimals ?? 8;

        // Try to parse the input as a number
        let formattedValue = value.replace(/,/g, ''); // Remove commas
        if (formattedValue.startsWith(".")) {
            formattedValue = `0${formattedValue}`; // Handle leading decimal
        }

        // Check if the input is a valid number (allows one decimal point)
        const isValidNumber = /^[0-9]*\.?[0-9]*$/.test(formattedValue);
        if (!isValidNumber) {
            setInputError(t("peertopeer_errors.invalid_value"));
            return;
        }

        // Restrict decimal places
        const parts = formattedValue.split('.');
        if (parts.length > 1 && parts[1].length > decimals) {
            formattedValue = `${parts[0]}.${parts[1].slice(0, decimals)}`;
            setInputValue(formattedValue); // Update UI to reflect truncation
        }

        // Parse as a number
        const numericValue = parseFloat(formattedValue) || 0;

        // Cap at available balance
        if (numericValue > availableBalance) {
            setInputError(t("amount_exceeds_balance"));
        } else {
            setWithdrawalAmount(numericValue);
            setInputError(null);
        }
    }, 500), [coinsData, selectedCoin, availableBalance]);

    // Handle input change (immediate update for display)
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        handleAmountChange(value);
    };

    const handleSubmit = async () => {
        try {
            if (!validAmount) {
                let min_limit = numberWithCommas(
                    limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.limits?.min_limit || 0,
                    coinsData?.coins[selectedCoin].decimals || 8
                );
                let max_limit = numberWithCommas(
                    limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.limits?.max_limit || 0,
                    coinsData?.coins[selectedCoin].decimals || 8
                );
                setInputError(t("limit_overflow") + ` ${min_limit} - ${max_limit} ${selectedCoin}`);
                return;
            }
            if (!selectedPaymentAccount) {
                setInputError(t("select_payment_account"));
                return;
            }
            setIsPending(true);
            setModalContent(<LoadingModal />);
            let requestParams = {
                blockchain_currency: selectedPaymentAccount.blockchain,
                currency: selectedPaymentAccount.currency,
                payment_account: selectedPaymentAccount.id,
                amount: withdrawalAmount,
            };
            if (coinsData.coins[selectedCoin].is_fiat) {
                const result = await withdrawalInswitchRequest({ ...requestParams, ...selectedPaymentAccount.fields });
                setIsPending(false);
                if (result.status === 201) {
                    setModalContent(
                        <ConfirmationCodeModal request={{ ...result.data, is_fiat: coinsData.coins[selectedCoin].is_fiat }} />
                    );
                } else {
                    closeModal();
                    setInputError(t("withdrawal_error_address"));
                }
            } else {
                const result = await withdrawalCryptoRequest(requestParams);
                setIsPending(false);
                if (result.status === 201) {
                    setModalContent(<ConfirmationCodeModal request={result.data} />);
                } else {
                    closeModal();
                    setInputError(t("withdrawal_error_address"));
                }
            }
        } catch (error: any) {
            closeModal();
            setIsPending(false);
            let errorMessage = error?.response?.data?.type?.[0]?.message || t("unexpected_error");
            errorMessage = error?.response?.data?.type?.message || errorMessage;
            setInputError(t(errorMessage) !== errorMessage ? t(errorMessage) : t("unexpected_error"));
            console.error("Error al hacer la solicitud a la API:", error);
        }
    };

    useEffect(() => {
        let errorMessage: string | null = null;

        if (inputError) {
            errorMessage = inputError;
        } else if (!validAmount && withdrawalAmount > 0) {
            const minLimit = limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.limits?.min_limit || 0;
            const maxLimit = limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.limits?.max_limit || Infinity;

            if (withdrawalAmount < minLimit) {
                errorMessage = t("amount_too_low");
            } else if (withdrawalAmount > maxLimit) {
                errorMessage = t("amount_too_high");
            } else {
                errorMessage = t("invalid_value");
            }
        }

        const timer = setTimeout(() => {
            setDisplayedError(errorMessage);
        }, 1000);

        return () => clearTimeout(timer);
    }, [
        inputError,
        validAmount,
        withdrawalAmount,
        limitsData,
        selectedCoin,
        paymentAccountKey,
    ]);

    useEffect(() => {
        if (inputError || (!validAmount && withdrawalAmount)) {
          const timer = setTimeout(() => {
            setShowDelayedError(true);
          }, 1000); // 1-second delay
          return () => clearTimeout(timer); // Cleanup on unmount
        } else {
          setShowDelayedError(false); // Reset if no error
        }
      }, [inputError, validAmount, withdrawalAmount]);

    return (
        <div className="withdrawal-main-cont">
            <div className="withdrawal-withdrawal-cont">
                <h1 className="withdrawal-title">{t("withdrawal")}</h1>
                <div className="withdrawal-body">
                    {!coinsData ? (
                        <Spinner />
                    ) : (
                        <>
                            <SelectAdvanced
                                label={t("select_coin")}
                                value={selectedCoin}
                                items={coinsItems()}
                                image={true}
                                onChange={handleCoinChange}
                            />
                            {coinsData && coinsData.coins && selectedCoin in coinsData.coins ? (
                                <div className="withdrawal-fields-content">
                                    <div className="withdrawal-field">
                                        <div className="withdrawal-field-label">{t("available_balance")}</div>
                                        <div className="withdrawal-field-value">
                                            {numberWithCommas(availableBalance, coinsData?.coins[selectedCoin].decimals)}
                                        </div>
                                    </div>
                                    {!coinsData ? (
                                        <div className="loading-spinner-container">
                                            <Spinner />
                                        </div>
                                    ) : filteredPaymentAccounts.length === 0 ? (
                                        <div
                                            onClick={() =>
                                                setModalContent(
                                                    <PaymentAccountModal ticker={selectedCoin} coin={coinsData.coins[selectedCoin]} />
                                                )
                                            }
                                            className="create-payment"
                                        >
                                            <div className="create-payment-text">{t("payment_account_modal.title")}</div>
                                            <FaCirclePlus className="add-payment-account-button" size={30} />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="payment-account-section">
                                                <div className="selectPayment">
                                                    <SelectAdvanced
                                                        value={selectedPaymentAccount ? selectedPaymentAccount.display_name : ""}
                                                        label={t("select_payment_account")}
                                                        items={paymentAccountsItems}
                                                        image={false}
                                                        onChange={handleSelectedPaymentAccountChange}
                                                    />
                                                </div>
                                                <div className="payment-account-action-content">
                                                    <div
                                                        onClick={() =>
                                                            setModalContent(
                                                                <PaymentAccountModal ticker={selectedCoin} coin={coinsData.coins[selectedCoin]} />
                                                            )
                                                        }
                                                        className="create-payment"
                                                    >
                                                        <FaCirclePlus className="add-payment-account-button" size={30} />
                                                    </div>
                                                    <div
                                                        onClick={() => {
                                                            if (!selectedPaymentAccount || selectedPaymentAccount?.type === "card") {
                                                                setInputError(t("select_payment_account"));
                                                                return;
                                                            }
                                                            setModalContent(
                                                                <PaymentAccountInfoModal
                                                                    fields={
                                                                        selectedPaymentAccount?.type === "fiat" ||
                                                                            selectedPaymentAccount?.type === "bank_transfer"
                                                                            ? {
                                                                                ...selectedPaymentAccount?.fields,
                                                                                ...{ fields: selectedPaymentAccount?.fields.data, id: selectedPaymentAccount?.id },
                                                                            }
                                                                            : selectedPaymentAccount?.type === "crypto"
                                                                                ? selectedPaymentAccount
                                                                                : { fields: selectedPaymentAccount }
                                                                    }
                                                                />
                                                            );
                                                        }}
                                                        className="create-payment"
                                                    >
                                                        <LuInspect className="inspect-payment-account-button" size={30} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="withdrawal-field">
                                                <div className="withdrawal-field-label">{t("amount")}</div>
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className={`withdrawal-amount-value ${showDelayedError ? "input-error" : ""
                                                            }`}
                                                        value={inputValue}
                                                        onChange={handleInputChange}
                                                        placeholder={
                                                            availableBalance === 0 ? t("no_balance") : t("enter_amount")
                                                        }
                                                        disabled={!selectedPaymentAccount || availableBalance === 0}
                                                        aria-label={t("withdrawal_amount")}
                                                        aria-describedby="amount-error"
                                                        inputMode="decimal"
                                                    />
                                                    <div className="withdrawal-quantiles-buttons">

                                                        <button
                                                            className="withdrawal-min-max-button"
                                                            onClick={() => {
                                                                setWithdrawalAmount(availableBalance * 0.50);
                                                                setInputValue((availableBalance * 0.50).toFixed(coinsData?.coins?.[selectedCoin]?.decimals).toString());
                                                                setInputError(null);
                                                            }}
                                                            disabled={!selectedPaymentAccount || availableBalance === 0}
                                                        >
                                                            {t("50%")}
                                                        </button>
                                                        <button
                                                            className="withdrawal-min-max-button"
                                                            onClick={() => {
                                                                setWithdrawalAmount(availableBalance * 0.75);
                                                                setInputValue((availableBalance * 0.75).toFixed(coinsData?.coins?.[selectedCoin]?.decimals).toString());
                                                                setInputError(null);
                                                            }}
                                                            disabled={!selectedPaymentAccount || availableBalance === 0}
                                                        >
                                                            {t("75%")}
                                                        </button>
                                                        <button
                                                            className="withdrawal-min-max-button"
                                                            onClick={() => {
                                                                setWithdrawalAmount(availableBalance);
                                                                setInputValue(availableBalance.toFixed(coinsData?.coins?.[selectedCoin]?.decimals).toString());
                                                                setInputError(null);
                                                            }}
                                                            disabled={!selectedPaymentAccount || availableBalance === 0}
                                                        >
                                                            {t("100%")}
                                                        </button>




                                                    </div>
                                                    <div id="amount-error" className="error-container">
                                                        {displayedError ? (
                                                            <p className="error-message">{displayedError}</p>
                                                        ) : (
                                                            <p className="error-placeholder"> </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <FeeLimits
                                                ticker={selectedCoin}
                                                amount={withdrawalAmount}
                                                minLimit={limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.limits?.min_limit || 0}
                                                maxLimit={limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.limits?.max_limit || 0}
                                                fee={limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.fees?.percentage_fee || 0}
                                                minFee={limitsData?.[selectedCoin]?.withdrawal?.[paymentAccountKey]?.fees?.min_fee || 0}
                                                flat={false}
                                                onLimitCheck={handleLimitCheck}
                                                isPending={isPending}
                                                buttonAction={handleSubmit}
                                                decimals={coinsData?.coins?.[selectedCoin]?.decimals}
                                            />
                                        </>
                                    )}
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
            <div className="withdrawal-history">
                <InOutHistory filterDefault="negative" />
            </div>
        </div>
    );
};

export default Withdraw;